import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export function EmailFrame({ html, testid }) {
  return <iframe title="Vista previa del correo" srcDoc={html || ""} sandbox="" className="h-[420px] w-full rounded-md border border-slate-200 bg-white" data-testid={testid} />;
}

export function TemplateEditor({ tpl, onClose, onSaved }) {
  const [v, setV] = useState({ subject: "", body: "", button_text: "", subject_en: "", body_en: "", button_text_en: "" });
  const [lang, setLangTab] = useState("es");
  const sfx = lang === "en" ? "_en" : "";
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (tpl) { setV({ subject: tpl.subject, body: tpl.body, button_text: tpl.button_text || "", subject_en: tpl.subject_en || "", body_en: tpl.body_en || "", button_text_en: tpl.button_text_en || "" }); setErr(""); }
  }, [tpl]);
  useEffect(() => {
    if (!tpl) { return undefined; }
    const t = setTimeout(() => {
      api.post(`/email-templates/${tpl.key}/preview`, { ...v, lang }).then((r) => setPreview(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [v, tpl, lang]);

  const save = async () => {
    setSaving(true); setErr("");
    try { await api.put(`/email-templates/${tpl.key}`, { ...v, button_text: v.button_text || null, button_text_en: v.button_text_en || null, enabled: tpl.enabled }); toast.success("Plantilla guardada"); onSaved(); onClose(); }
    catch (e) { setErr(errMsg(e)); } finally { setSaving(false); }
  };
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Dialog open={Boolean(tpl)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="font-display">Editar plantilla: {tpl?.name}</DialogTitle>
          <DialogDescription>{tpl?.event}. Separe los párrafos con una línea en blanco. Las variables sin dato se muestran como [PENDIENTE].</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="inline-flex rounded-full border border-slate-200 p-0.5 text-xs font-semibold" data-no-translate>{[["es", "Español"], ["en", "English"]].map(([l, t]) => <button key={l} type="button" onClick={() => setLangTab(l)} className={`rounded-full px-3 py-1 ${lang === l ? "bg-blue-600 text-white" : "text-slate-500"}`} data-testid={`template-lang-${l}`}>{t}</button>)}</div>
            <p className="text-xs text-slate-500">Cada cliente recibe la versión del idioma que eligió en su cuenta.</p>
            <div><label className="field-label" htmlFor="tpl-subject">Asunto</label><input id="tpl-subject" className="field-input" value={v[`subject${sfx}`]} onChange={set(`subject${sfx}`)} data-testid="template-subject-input" /></div>
            <div><label className="field-label" htmlFor="tpl-body">Mensaje</label><textarea id="tpl-body" rows={10} className="field-input" value={v[`body${sfx}`]} onChange={set(`body${sfx}`)} data-testid="template-body-input" /></div>
            <div><label className="field-label" htmlFor="tpl-btn">Texto del botón al portal (vacío = sin botón)</label><input id="tpl-btn" className="field-input" value={v[`button_text${sfx}`]} onChange={set(`button_text${sfx}`)} data-testid="template-button-input" /></div>
            <div><p className="field-label">Variables disponibles</p><div className="flex flex-wrap gap-1.5">{(tpl?.variables || []).map((x) => (
              <button type="button" key={x} onClick={() => setV((s) => ({ ...s, [`body${sfx}`]: `${s[`body${sfx}`]}{{${x}}}` }))} className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs hover:bg-blue-50" data-testid={`template-var-${x}`}>{`{{${x}}}`}</button>))}</div></div>
          </div>
          <div>
            <p className="field-label">Vista previa (datos de ejemplo)</p>
            <p className="mb-2 truncate text-sm font-semibold text-slate-800" data-testid="template-preview-subject">{preview?.subject}</p>
            <EmailFrame html={preview?.html} testid="template-preview-frame" />
          </div>
        </div>
        {err && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="template-editor-error">{err}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="template-editor-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="template-editor-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
